import { useMemo, useRef, useState } from "react";
import { useScrollTopOnChange } from "@/hooks/use-scroll-top-on-change";
import { Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowRight,
  Brain,
  Check,
  ChevronDown,
  ChevronRight,
  CloudRain,
  Flame,
  HandHeart,
  HeartCrack,
  HeartHandshake,
  HelpCircle,
  Anchor,
  BookOpen,
  Leaf,
  LifeBuoy,
  Lock,
  MessageCircle,
  Moon,
  Palette,
  Shield,
  ShieldHalf,
  Snowflake,
  Sparkles,
  Sprout,
  User,
  UserRound,
  Users,
  Waves,
  X,
  RotateCcw,
  type LucideIcon,
} from "lucide-react";
import { SupportOptions } from "@/components/feelings/SupportOptions";
import { CrisisDialog } from "@/components/CrisisDialog";
import { assessCrisis } from "@/lib/crisis-detection";
import { EmotionWheel } from "@/components/feelings/EmotionWheel";
import { promptsFor, resetFor } from "@/data/feelings-support";
import {
  BODY_FALLBACK_PRACTICES,
  BODY_SIGNS,
  CORE_FEELINGS,
  NEEDS,
  SAFETY_NOTE,
  UNKNOWN_COLOR,
  getFeeling,
  ink,
  orderPractices,
  tint,
  type CoreFeeling,
  type NeedKey,
  type Practice,
} from "@/data/feelings-pathway";

const ICONS: Record<string, LucideIcon> = {
  "user-round": UserRound,
  "hand-heart": HandHeart,
  "cloud-rain": CloudRain,
  brain: Brain,
  flame: Flame,
  users: Users,
  "heart-crack": HeartCrack,
  waves: Waves,
  snowflake: Snowflake,
  shield: Shield,
  leaf: Leaf,
  user: User,
  "heart-handshake": HeartHandshake,
  "message-circle": MessageCircle,
  moon: Moon,
  "shield-half": ShieldHalf,
  sparkles: Sparkles,
  palette: Palette,
  sprout: Sprout,
  anchor: Anchor,
  "life-buoy": LifeBuoy,
};

type Step =
  | "choose"
  | "body"
  | "secondary"
  | "explore"
  | "support"
  | "reflect"
  | "needs"
  | "practices";

export type CheckInSummary = {
  feelingKey: string;
  feelingLabel: string;
  secondary: string | null;
  needs: string[];
  practice: string | null;
};

export type FeelingsPathwayProps = {
  /** "preview" = short public taste of the pathway; "full" = whole journey. */
  variant?: "full" | "preview";
  /** Called when a core feeling is chosen. */
  onFeelingChosen?: (feelingKey: string) => void;
  /** When provided, members are offered an optional private save at the end. */
  onSaveCheckIn?: (summary: CheckInSummary) => Promise<void> | void;
  /** Members can optionally save their reflection to their private journal. */
  onSaveReflection?: (entry: { title: string; content: string; mood: string }) => Promise<void> | void;
  /** Guests see member-only practices as a locked invitation. */
  hasMemberAccess?: boolean;
  /** Tighter spacing and a smaller wheel (homepage / dashboard teaser). */
  compact?: boolean;
  /** When true, the preview step shows a desktop side panel explaining the wheel. */
  explainWheel?: boolean;
  /** Shows the guided check-in introduction card above the wheel. */
  showIntro?: boolean;
  /** On wide screens, places the introduction to the left of the wheel. */
  desktopIntroBesideWheel?: boolean;
  headline?: string;
  subtext?: string;
};

/** Titled introduction + expandable "How it works" shown above the wheel. */
function WheelIntro() {
  const [open, setOpen] = useState(false);
  return (
    <div className="px-1 pt-1">
      <h2
        id="feelings-pathway-heading"
        className="font-display text-[1.7rem] leading-tight text-foreground sm:text-3xl"
      >
        🌈 How are you feeling right now?
      </h2>
      <p className="mt-1.5 text-[15px] leading-snug text-foreground/85">
        Tap a feeling to check in with yourself and find a supportive next step.
      </p>


      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="mt-3 inline-flex min-h-9 items-center gap-1.5 rounded-full border border-gold/35 bg-card px-3.5 text-xs font-semibold text-foreground transition hover:border-gold"
      >
        How it works
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>

      {open && (
        <div className="mt-3 animate-fade-in">
          <p className="mb-3 text-[13px] leading-relaxed text-muted-foreground">
            Sometimes you know something feels “off,” but you can't quite name it. The Emotion Wheel
            helps you slow down, put words to what you're feeling, and choose a supportive next step.
          </p>
          <ol className="space-y-2">
            {[
              ["Notice", "Take a moment to notice what you're feeling in your body and mind."],
              ["Choose", "Tap the emotion on the wheel that feels closest to right now."],
              ["Explore", "Discover the more specific feelings connected to that emotion."],
              ["Understand", "Read a short explanation of what the emotion may be communicating."],
              ["Support yourself", "Choose a suggested grounding, reflection or regulation practice."],
            ].map(([title, body], i) => (
              <li key={title} className="flex gap-2.5">
                <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-gold/25 text-[11px] font-semibold text-[var(--gold-deep)]">
                  {i + 1}
                </span>
                <p className="text-[13px] leading-relaxed text-foreground/85">
                  <span className="font-semibold text-foreground/90">{title}</span>
                  {` — ${body}`}
                </p>
              </li>
            ))}
          </ol>
          <p className="mt-3 text-[13px] leading-relaxed text-muted-foreground">
            <span className="font-semibold text-foreground/90">Why use it? </span>
            Regular check-ins can help you build emotional awareness, recognise patterns and
            triggers, and put words to difficult experiences — so you can find what might support
            you in that moment.
          </p>
        </div>
      )}

    </div>
  );
}

function CrisisLink() {
  return (
    <Link
      to="/crisis"
      className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
    >
      <LifeBuoy className="h-3.5 w-3.5" aria-hidden />
      I need support right now
    </Link>
  );
}

/** Colourful emotion card — the mobile equivalent of a wheel segment. */
function FeelingCard({
  label,
  hint,
  color,
  icon,
  onSelect,
  selected,
  compact,
}: {
  label: string;
  hint?: string;
  color: string;
  icon?: string;
  onSelect: () => void;
  selected?: boolean;
  compact?: boolean;
}) {
  const Icon = icon ? (ICONS[icon] ?? Sparkles) : Sparkles;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      style={{
        background: `linear-gradient(150deg, ${tint(color, 22)}, ${tint(color, 52)})`,
        borderColor: selected ? ink(color) : `color-mix(in oklab, ${color} 45%, white)`,
        color: ink(color),
      }}
      className={`on-tint group flex w-full flex-col items-start justify-between gap-2 rounded-2xl border-2 p-3.5 text-start transition duration-200 hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gold-deep)] ${
        compact ? "min-h-[76px]" : "min-h-[96px]"
      }`}
    >
      <span className="flex w-full items-center justify-between">
        <Icon className="h-5 w-5" aria-hidden strokeWidth={1.9} />
        {selected && <Check className="h-4 w-4" aria-hidden />}
      </span>
      <span>
        <span className="block text-[15px] font-semibold leading-snug">{label}</span>
        {hint && !compact && (
          <span className="mt-0.5 block text-[11px] font-medium leading-snug opacity-80">{hint}</span>
        )}
      </span>
    </button>
  );
}

function PracticeCard({
  practice,
  locked,
  color,
  onOpen,
}: {
  practice: Practice;
  locked: boolean;
  color: string;
  onOpen?: () => void;
}) {
  const style = {
    background: `linear-gradient(160deg, ${tint(color, 12)}, ${tint(color, 34)})`,
    borderColor: `color-mix(in oklab, ${color} 40%, white)`,
  };
  if (locked) {
    return (
      <div className="on-tint flex h-full flex-col justify-between rounded-2xl border-2 p-4" style={style}>
        <div>
          <p className="inline-flex items-center gap-1.5 text-[15px] font-semibold" style={{ color: ink(color) }}>
            <Lock className="h-3.5 w-3.5" aria-hidden />
            {practice.label}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-foreground/75">{practice.blurb}</p>
        </div>
        <Link
          to="/membership"
          className="mt-3 inline-flex items-center gap-1 text-xs font-semibold"
          style={{ color: ink(color) }}
        >
          Open with membership <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </Link>
      </div>
    );
  }
  return (
    <Link
      to={practice.to}
      onClick={onOpen}
      style={style}
      className="on-tint flex h-full flex-col justify-between rounded-2xl border-2 p-4 transition hover:-translate-y-0.5 hover:shadow-md"
    >
      <div>
        <p className="text-[15px] font-semibold" style={{ color: ink(color) }}>
          {practice.label}
        </p>
        <p className="mt-1 text-xs leading-relaxed text-foreground/75">{practice.blurb}</p>
      </div>
      <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold" style={{ color: ink(color) }}>
        Try this <ArrowRight className="h-3.5 w-3.5" aria-hidden />
      </span>
    </Link>
  );
}

/** Roughly half of a list — used for the free preview of member-only content. */
function previewHalf(items: readonly string[]) {
  return items.slice(0, Math.max(2, Math.ceil(items.length / 2)));
}

/** Blurred taste of the member-only journey, with a gentle invitation. */
function LockedPreview({ color, onReset }: { color: string; onReset: () => void }) {
  const items = [
    { icon: BookOpen, label: "Take a Moment to Reflect", line: "4–6 gentle prompts written for this feeling." },
    { icon: HandHeart, label: "What Might You Need Right Now?", line: "Tap a need to see what it can look like today." },
    { icon: Sparkles, label: "Your Recovery Trauma™ Exercise", line: "A 60-second reset for exactly this feeling." },
    { icon: Lock, label: "Save to Journal & full resources", line: "Keep your reflection privately, and open every practice." },
  ];
  return (
    <div className="relative overflow-hidden rounded-2xl border-2 border-gold/25">
      {/* Blurred taste of the member journey sits behind the invitation. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 select-none space-y-3 p-4 blur-[5px]"
      >
        {items.map((i) => (
          <div
            key={i.label}
            className="on-tint rounded-2xl border-2 p-4"
            style={{ background: tint(color, 16), borderColor: `color-mix(in oklab, ${color} 35%, white)` }}
          >
            <p className="text-[15px] font-semibold" style={{ color: ink(color) }}>
              {i.label}
            </p>
            <p className="mt-1 text-xs text-foreground/75">{i.line}</p>
          </div>
        ))}
      </div>
      <div
        className="absolute inset-0"
        style={{ background: "oklch(0.97 0.015 85 / 0.97)" }}
        aria-hidden
      />
      {/* Content is in normal flow, so the card grows with it on any width. */}
      <div className="on-tint relative flex flex-col items-center gap-4 px-5 py-9 text-center sm:px-6 sm:py-10">
        <span
          className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-full"
          style={{ background: tint(color, 40), color: ink(color) }}
        >
          <Lock className="h-5 w-5" aria-hidden />
        </span>
        <p className="max-w-md text-balance font-display text-xl font-semibold leading-snug text-foreground sm:text-2xl">
          Continue with Recovery Trauma™
        </p>

        <p className="max-w-md text-sm leading-relaxed text-foreground/80">
          Understand what may be underneath this feeling, what can trigger it, how it can affect your
          body and relationships, and explore practical tools that may help you regulate.
        </p>
        <ul className="flex flex-wrap items-center justify-center gap-2">
          {items.map((i) => (
            <li
              key={i.label}
              className="inline-flex items-center gap-1.5 rounded-full border border-gold/30 bg-card/80 px-3 py-1.5 text-[11px] font-medium text-foreground/80"
            >
              <i.icon className="h-3.5 w-3.5 flex-none" aria-hidden />
              {i.label}
            </li>
          ))}
        </ul>
        <div className="flex w-full flex-col items-center gap-2.5 sm:w-auto sm:flex-row sm:justify-center">
          <Link
            to="/membership"
            className="inline-flex min-h-[48px] w-full max-w-xs items-center justify-center gap-2 rounded-full bg-[var(--gold-deep)] px-6 text-sm font-semibold text-[var(--cream,#FBF7EF)] transition hover:-translate-y-0.5 sm:w-auto"
          >
            Join Recovery Trauma™ <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
          <Link
            to="/login"
            className="inline-flex min-h-[48px] w-full max-w-xs items-center justify-center rounded-full border border-gold/40 bg-card px-5 text-sm font-semibold text-foreground sm:w-auto"
          >
            Log In
          </Link>
          <button
            type="button"
            onClick={onReset}
            className="inline-flex min-h-[48px] w-full max-w-xs items-center justify-center rounded-full border border-gold/30 px-5 text-sm font-medium text-foreground sm:w-auto"
          >
            Choose another feeling
          </button>
        </div>
      </div>
    </div>
  );
}

export function FeelingsPathway({
  variant = "full",
  onFeelingChosen,
  onSaveCheckIn,
  onSaveReflection,
  hasMemberAccess = true,
  compact = false,
  explainWheel = false,
  showIntro = false,
  desktopIntroBesideWheel = false,
  headline = "How are you feeling right now?",
  subtext = "Choose what feels closest. We'll help you explore it.",
}: FeelingsPathwayProps) {
  const [step, setStep] = useState<Step>("choose");
  // Embedded on Home/Dashboard: scroll to the wheel section, not the page top.
  const sectionRef = useRef<HTMLElement | null>(null);
  useScrollTopOnChange(step, sectionRef);
  const [feeling, setFeeling] = useState<CoreFeeling | null>(null);
  const [secondary, setSecondary] = useState<string | null>(null);
  const [bodySign, setBodySign] = useState<string | null>(null);
  const [needs, setNeeds] = useState<NeedKey[]>([]);
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [crisis, setCrisis] = useState(false);
  const [openedPractice, setOpenedPractice] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [journalState, setJournalState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const isPreview = variant === "preview";
  // Members and admins always see the full content — even in the homepage preview.
  const unlocked = hasMemberAccess;
  const prompts = feeling ? promptsFor(feeling) : [];
  const resetScript = resetFor(feeling);
  const accent = feeling?.color ?? UNKNOWN_COLOR;

  const practices = useMemo(() => {
    const base = feeling ? feeling.practices : BODY_FALLBACK_PRACTICES;
    return orderPractices(base, needs);
  }, [feeling, needs]);

  const resetAnswers = () => {
    setSecondary(null);
    setNeeds([]);
    setNotes({});
    setOpenedPractice(null);
    setSaveState("idle");
    setJournalState("idle");
  };

  /** Desktop wheel: first tap highlights and opens the outer ring. */
  const highlight = (f: CoreFeeling) => {
    if (feeling?.key === f.key) return;
    setFeeling(f);
    resetAnswers();
    onFeelingChosen?.(f.key);
  };

  /** Mobile cards and body-first route: go straight on. */
  const choose = (f: CoreFeeling) => {
    setFeeling(f);
    resetAnswers();
    onFeelingChosen?.(f.key);
    setStep("explore");
  };

  const chooseSecondary = (f: CoreFeeling, s: string) => {
    if (feeling?.key !== f.key) {
      setFeeling(f);
      resetAnswers();
      onFeelingChosen?.(f.key);
    }
    setSecondary(s);
    setStep("explore");
  };

  const reset = () => {
    setStep("choose");
    setFeeling(null);
    setBodySign(null);
    resetAnswers();
  };

  const writeNote = (i: number, value: string) => {
    setNotes((n) => ({ ...n, [i]: value }));
    if (assessCrisis(value).detected) setCrisis(true);
  };

  const toggleNeed = (key: NeedKey) =>
    setNeeds((n) => (n.includes(key) ? n.filter((k) => k !== key) : [...n, key]));

  const back = () => {
    if (step === "explore") setStep("choose");
    else if (step === "secondary" || step === "body") setStep("choose");
    else if (step === "support") setStep("explore");
    else if (step === "reflect") setStep("support");
    else if (step === "needs") setStep("support");
    else if (step === "practices") setStep("needs");
  };

  const written = Object.entries(notes).filter(([, v]) => v.trim().length > 0);
  const hasWritten = written.length > 0;

  const saveReflection = async () => {
    if (!onSaveReflection || !feeling) return;
    setJournalState("saving");
    try {
      const body = written
        .map(([i, v]) => `${prompts[Number(i)] ?? ""}\n${v.trim()}`)
        .join("\n\n");
      await onSaveReflection({
        title: `How I'm feeling: ${feeling.label}${secondary ? ` · ${secondary}` : ""}`,
        content: body,
        mood: feeling.label,
      });
      setJournalState("saved");
    } catch {
      setJournalState("error");
    }
  };

  const save = async () => {
    if (!onSaveCheckIn || !feeling) return;
    setSaveState("saving");
    try {
      await onSaveCheckIn({
        feelingKey: feeling.key,
        feelingLabel: feeling.label,
        secondary,
        needs: needs.map((k) => NEEDS.find((n) => n.key === k)?.label ?? k),
        practice: openedPractice,
      });
      setSaveState("saved");
    } catch {
      setSaveState("error");
    }
  };

  return (
    <section
      ref={sectionRef}
      aria-labelledby="feelings-pathway-heading"
      className={
        step === "choose"
          ? "px-1 py-2 sm:rounded-[24px] sm:border sm:border-gold/25 sm:bg-card sm:p-5 sm:shadow-sm"
          : `rounded-[24px] border border-gold/25 bg-card shadow-sm ${compact ? "p-4 sm:p-5" : "p-5 sm:p-6"}`
      }
      style={{
        backgroundImage: feeling
          ? `radial-gradient(120% 80% at 50% 0%, ${tint(accent, 10)}, transparent 70%)`
          : undefined,
      }}
    >
      {crisis && <CrisisDialog onClose={() => setCrisis(false)} />}

      <div className="flex flex-wrap items-start justify-between gap-3">
        {/* The wheel repeats this heading in its centre on desktop. */}
        <div className={step === "choose" ? (showIntro ? "sr-only" : "md:sr-only") : undefined}>
          <h2
            id={showIntro && step === "choose" ? undefined : "feelings-pathway-heading"}
            className={`font-display text-foreground ${compact ? "text-lg sm:text-xl" : "text-xl sm:text-2xl"}`}
          >
            {headline}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">{subtext}</p>
        </div>
        {step !== "choose" && (
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={back}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-gold/30 px-3.5 text-xs font-medium text-foreground transition hover:border-gold"
            >
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
              Back
            </button>
            <button
              type="button"
              onClick={reset}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-gold/30 px-3.5 text-xs font-medium text-foreground transition hover:border-gold"
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden />
              Back to the wheel
            </button>
            <button
              type="button"
              onClick={reset}
              aria-label="Close and return to the wheel"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gold/30 text-foreground transition hover:border-gold"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
        )}
      </div>

      {step === "choose" && (
        <div
          className={
            desktopIntroBesideWheel
              ? "md:grid md:grid-cols-[minmax(280px,0.72fr)_minmax(0,1.28fr)] md:items-center md:gap-8"
              : undefined
          }
        >
          {showIntro && <WheelIntro />}

          <div className={compact ? "mt-2.5 md:mt-0" : explainWheel && isPreview ? "mt-2.5" : "mt-3 sm:mt-5"}>
            <div className={explainWheel && isPreview ? "lg:grid lg:grid-cols-12 lg:gap-4" : undefined}>
              <div className={explainWheel && isPreview ? "lg:col-span-8" : undefined}>
              <EmotionWheel
                selected={feeling}
                onSelectCore={highlight}
                onSelectSecondary={chooseSecondary}
                onUnknown={() => setStep("body")}
                compact={compact}
                showSecondaryRing={!isPreview}
              />

                {feeling && (
                  <div className="flex justify-center">
                    <button
                      type="button"
                      onClick={() => setStep("explore")}
                      style={{ background: ink(feeling.color) }}
                      className="mt-2 inline-flex min-h-[48px] animate-fade-in items-center gap-2 rounded-full px-6 text-sm font-semibold text-[var(--cream,#FBF7EF)] shadow-md transition hover:-translate-y-0.5 motion-reduce:transform-none"
                    >
                      {isPreview
                        ? `What ${feeling.label.toLowerCase()} can feel like`
                        : "Help me understand this feeling"}
                      <ArrowRight className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                )}

                <p className="mt-1 text-center text-[10px] text-muted-foreground md:hidden">
                  Tap the feeling closest to yours.
                </p>

              </div>

              {explainWheel && isPreview && (
                <div className="hidden lg:col-span-4 lg:block">
                  <div className="rounded-2xl border border-gold/20 bg-cream/60 p-4">
                    <h3 className="font-display text-base text-foreground">What is this wheel?</h3>
                    <p className="mt-1.5 text-sm leading-snug text-muted-foreground">
                      The Recovery Trauma™ Emotion Wheel is a gentle starting point, not a test.
                      Choose the feeling that feels closest right now.
                    </p>
                    <ul className="mt-2 space-y-1.5 text-sm text-foreground/85">
                      <li className="flex items-start gap-2">
                        <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-[var(--gold-deep)]" aria-hidden />
                        Name what you're feeling without forcing it into a box.
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-[var(--gold-deep)]" aria-hidden />
                        Works whether you feel clear, mixed, or "I don't know."
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-[var(--gold-deep)]" aria-hidden />
                        Members explore what's underneath and get a matched exercise.
                      </li>
                    </ul>
                    <p className="mt-2 text-xs leading-snug text-muted-foreground">
                      This is for self-reflection only. It doesn't diagnose autism, PTSD, C-PTSD or any other condition.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 space-y-2.5">
              <button
                type="button"
                onClick={() => setStep("body")}
                className="flex w-full items-center gap-3 rounded-2xl border border-gold/30 bg-card/60 px-4 py-3.5 text-start transition hover:border-gold"
              >
                <Sparkles className="h-6 w-6 shrink-0 text-[var(--gold-deep)]" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] font-semibold text-foreground">Not sure what you're feeling?</span>
                  <span className="block text-xs text-muted-foreground">A few questions can help you find it.</span>
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              </button>
              <Link
                to="/crisis"
                className="flex w-full items-center gap-3 rounded-2xl border border-gold/30 bg-card/60 px-4 py-3.5 transition hover:border-gold"
              >
                <LifeBuoy className="h-6 w-6 shrink-0 text-[var(--gold-deep)]" aria-hidden />
                <span className="min-w-0 flex-1 text-[15px] font-medium text-foreground">I need support right now</span>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              </Link>
            </div>
            <p className="mt-3 px-1 text-[11px] leading-relaxed text-muted-foreground">
              This is for education and self-reflection only. It isn't an assessment, a diagnosis or
              therapy, and it can't tell you why you feel something — only you can know that. {SAFETY_NOTE}
            </p>
          </div>
        </div>
      )}

      {/* ── Body-first pathway ───────────────────────────────────── */}
      {step === "body" && (
        <div className="mt-5">
          <p className="text-sm leading-relaxed text-foreground/85">
            That's okay. Let's start with what you notice in your body. There's no right answer,
            and you can change your mind.
          </p>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {BODY_SIGNS.map((sign) => (
              <button
                key={sign.key}
                type="button"
                onClick={() => setBodySign(sign.key)}
                aria-pressed={bodySign === sign.key}
                style={{
                  background: `linear-gradient(150deg, ${tint(sign.color, bodySign === sign.key ? 34 : 16)}, ${tint(
                    sign.color,
                    bodySign === sign.key ? 62 : 40,
                  )})`,
                  borderColor:
                    bodySign === sign.key ? ink(sign.color) : `color-mix(in oklab, ${sign.color} 40%, white)`,
                }}
                className="on-tint min-h-[84px] rounded-2xl border-2 p-3.5 text-start transition hover:-translate-y-0.5 hover:shadow-md"
              >
                <span className="block text-[15px] font-semibold" style={{ color: ink(sign.color) }}>
                  {sign.label}
                </span>
                <span className="mt-0.5 block text-[11px] leading-snug text-foreground/75">{sign.line}</span>
              </button>
            ))}
          </div>

          {bodySign && (
            <div className="mt-5 rounded-2xl border border-gold/20 bg-cream/40 p-4">
              {(() => {
                const sign = BODY_SIGNS.find((s) => s.key === bodySign)!;
                const maybe = sign.maybe.map(getFeeling).filter(Boolean) as CoreFeeling[];
                if (maybe.length === 0) {
                  return (
                    <>
                      <p className="text-sm leading-relaxed text-foreground/85">
                        That's completely valid. Noticing takes practice, and it often comes back
                        slowly. Something gentle and grounding may be a kind place to begin.
                      </p>
                      <button
                        type="button"
                        onClick={() => setStep("practices")}
                        className="mt-3 inline-flex min-h-[48px] items-center gap-1.5 rounded-full bg-[var(--gold-deep)] px-5 text-sm font-semibold text-[var(--cream,#FBF7EF)]"
                      >
                        See gentle practices <ArrowRight className="h-4 w-4" aria-hidden />
                      </button>
                    </>
                  );
                }
                return (
                  <>
                    <p className="text-sm leading-relaxed text-foreground/85">
                      Sometimes people notice this sensation alongside the feelings below. Do any of
                      those feel close? It's completely fine if none of them do.
                    </p>
                    <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                      {maybe.map((f) => (
                        <FeelingCard
                          key={f.key}
                          label={f.label}
                          color={f.color}
                          icon={f.icon}
                          compact
                          onSelect={() => choose(f)}
                        />
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => setStep("practices")}
                      className="mt-3 min-h-[44px] text-xs font-medium text-muted-foreground underline-offset-4 hover:underline"
                    >
                      None of these — show me something gentle instead
                    </button>
                  </>
                );
              })()}
            </div>
          )}
          <div className="mt-4">
            <CrisisLink />
          </div>
        </div>
      )}

      {/* ── Step 2: secondary feelings ───────────────────────────── */}
      {step === "secondary" && feeling && (
        <div className="mt-5">
          <p className="text-sm leading-relaxed text-foreground/85">
            Does anything here feel closer than <strong>{feeling.label.toLowerCase()}</strong>?
            You can also skip this step.
          </p>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {feeling.secondary.map((s, i) => (
              <button
                key={s}
                type="button"
                onClick={() => chooseSecondary(feeling, s)}
                style={{
                  background: `linear-gradient(150deg, ${tint(feeling.color, 18 + i * 6)}, ${tint(
                    feeling.color,
                    46 + i * 6,
                  )})`,
                  borderColor: `color-mix(in oklab, ${feeling.color} 42%, white)`,
                  color: ink(feeling.color),
                  animationDelay: `${i * 50}ms`,
                }}
                className="on-tint min-h-[64px] animate-fade-in rounded-2xl border-2 px-4 py-3 text-[15px] font-semibold transition hover:-translate-y-0.5 hover:shadow-md"
              >
                {s}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setStep("explore")}
            style={{ background: ink(feeling.color) }}
            className="mt-5 inline-flex min-h-[48px] items-center gap-2 rounded-full px-6 text-sm font-semibold text-[var(--cream,#FBF7EF)]"
          >
            Help me understand this feeling <ArrowRight className="h-4 w-4" aria-hidden />
          </button>
        </div>
      )}

      {/* ── Step 3: explore ──────────────────────────────────────── */}
      {step === "explore" && feeling && (
        <div className="mt-5 space-y-5">
          <div
            className="on-tint rounded-2xl border-2 p-4"
            style={{
              background: `linear-gradient(160deg, ${tint(feeling.color, 14)}, ${tint(feeling.color, 38)})`,
              borderColor: `color-mix(in oklab, ${feeling.color} 40%, white)`,
            }}
          >
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: ink(feeling.color) }}>
              {secondary ? `${feeling.label} · ${secondary}` : feeling.label}
            </p>
            <h3 className="mt-2 font-display text-lg" style={{ color: "var(--question-ink)" }}>
              You're feeling {feeling.label.toLowerCase()}
            </h3>
            <p className="mt-1.5 text-sm leading-relaxed" style={{ color: "color-mix(in oklab, var(--question-ink) 88%, transparent)" }}>{feeling.feelsLike}</p>
          </div>

          <div>
            <h3 className="font-display text-lg text-foreground">
              Sometimes this can be connected to…
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              See whether any of these feel relevant to you. This may or may not fit your experience.
            </p>
            <ul className="mt-3 space-y-2">
              {(unlocked
                ? isPreview
                  ? feeling.connectedTo.slice(0, 3)
                  : feeling.connectedTo
                : previewHalf(feeling.connectedTo)
              ).map((c) => (
                <li key={c} className="flex gap-2 text-sm leading-relaxed text-foreground/85">
                  <span
                    aria-hidden
                    className="mt-2 h-1.5 w-1.5 flex-none rounded-full"
                    style={{ background: ink(feeling.color) }}
                  />
                  {c}
                </li>
              ))}
            </ul>
          </div>

          {unlocked && (
          <div>
            <h3 className="font-display text-lg text-foreground">
              Possible feelings underneath it
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Some people find a closer word here. This may or may not fit your experience.
            </p>
            <ul className="mt-3 flex flex-wrap gap-2">
              {feeling.secondary.map((sec) => {
                const chosen = secondary === sec;
                return (
                  <li key={sec}>
                    <button
                      type="button"
                      aria-pressed={chosen}
                      onClick={() => setSecondary(chosen ? null : sec)}
                      className={`min-h-[36px] rounded-full border px-3 py-1.5 text-xs font-medium transition hover:-translate-y-0.5${chosen ? " rt-choice" : ""}`}
                      style={{
                        background: tint(feeling.color, chosen ? 46 : 22),
                        borderColor: `color-mix(in oklab, ${feeling.color} ${chosen ? 70 : 38}%, white)`,
                        color: ink(feeling.color),
                      }}
                    >
                      {sec}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
          )}

          <div>
            <h3 className="font-display text-lg text-foreground">You might notice…</h3>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {(
                [
                  ["In your feelings", unlocked ? feeling.notice.emotional : previewHalf(feeling.notice.emotional)],
                  ["In what you do", unlocked ? feeling.notice.behavioural : previewHalf(feeling.notice.behavioural)],
                  ["In your body", unlocked ? feeling.notice.body : previewHalf(feeling.notice.body)],
                ] as [string, string[]][]
              ).map(([title, items]) => (
                <div
                  key={title}
                  className="rounded-2xl border-2 p-4"
                  style={{
                    background: tint(feeling.color, 10),
                    borderColor: `color-mix(in oklab, ${feeling.color} 30%, white)`,
                  }}
                >
                  <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: ink(feeling.color) }}>
                    {title}
                  </p>
                  <ul className="mt-2 space-y-1.5">
                    {items.map((i) => (
                      <li key={i} className="text-sm leading-relaxed text-foreground/85">
                        {i}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {unlocked && (
            <div className="rounded-2xl border border-gold/25 bg-cream/50 p-4">
              <p className="text-sm font-medium text-foreground">Ready for something supportive?</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                You don't have to read or write anything else. The next screen offers a few gentle
                things you can do right now — grounding, breathing, a kind word, or simply
                listening. Reflection is there too, if and when you want it.
              </p>
              <button
                type="button"
                onClick={() => setStep("support")}
                style={{ background: ink(feeling.color) }}
                className="mt-3 inline-flex min-h-[48px] items-center gap-2 rounded-full px-6 text-sm font-semibold text-[var(--cream,#FBF7EF)] transition hover:-translate-y-0.5"
              >
                What might I need right now? <ArrowRight className="h-4 w-4" aria-hidden />
              </button>
            </div>
          )}

          {!unlocked && <LockedPreview color={feeling.color} onReset={reset} />}

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gold/15 pt-4">
            <p className="max-w-xl text-[11px] leading-relaxed text-muted-foreground">{SAFETY_NOTE}</p>
            <CrisisLink />
          </div>
        </div>
      )}

      {/* ── Step 4: immediate support ────────────────────────────── */}
      {step === "support" && (
        <div className="mt-5 space-y-5">
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Emotion wheel {feeling ? `· ${feeling.label}` : ""} · What might help right now?
          </p>

          <SupportOptions
            feeling={feeling}
            hasMemberAccess={hasMemberAccess}
            onReflect={() => setStep("reflect")}
            onHelpMeChoose={() => setStep("needs")}
            onOpenPractice={setOpenedPractice}
          />

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setStep("explore")}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-gold/30 px-4 text-xs font-medium text-foreground transition hover:border-gold"
            >
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
              {feeling ? `Back to ${feeling.label.toLowerCase()}` : "Back"}
            </button>
            <button
              type="button"
              onClick={reset}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-gold/30 px-4 text-xs font-medium text-foreground transition hover:border-gold"
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden />
              Back to the wheel
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gold/15 pt-4">
            <p className="max-w-xl text-[11px] leading-relaxed text-muted-foreground">{SAFETY_NOTE}</p>
            <CrisisLink />
          </div>
        </div>
      )}

      {/* ── Optional: reflection prompts ─────────────────────────── */}
      {step === "reflect" && feeling && (
        <div className="mt-5 space-y-4">
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Emotion wheel · {feeling.label} · Reflect
          </p>
          <div>
            <h3 className="font-display text-lg text-foreground">Take a moment to reflect</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Only if you'd like to. You can answer one question, or none. Nothing is kept unless
              you choose to save it to your journal.
            </p>
            <div className="mt-3 space-y-3">
              {prompts.map((prompt, i) => (
                <label key={prompt} className="block">
                  <span className="text-sm text-foreground/85">{prompt}</span>
                  <textarea
                    value={notes[i] ?? ""}
                    onChange={(e) => writeNote(i, e.target.value)}
                    rows={2}
                    className="mt-1.5 w-full rounded-2xl border border-gold/25 bg-background p-3 text-sm text-foreground outline-none focus:border-gold"
                    placeholder="Write as much or as little as you want…"
                  />
                </label>
              ))}
            </div>

            {onSaveReflection && (
              <div className="mt-3 flex flex-wrap items-center gap-3">
                {journalState === "saved" ? (
                  <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--gold-deep)]">
                    <Check className="h-4 w-4" aria-hidden /> Saved to your private journal
                  </p>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={saveReflection}
                      disabled={journalState === "saving" || !hasWritten}
                      className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border-2 px-5 text-sm font-semibold disabled:opacity-50"
                      style={{ borderColor: ink(feeling.color), color: ink(feeling.color) }}
                    >
                      <BookOpen className="h-4 w-4" aria-hidden />
                      {journalState === "saving" ? "Saving…" : "Save this to my journal"}
                    </button>
                    <span className="text-xs text-muted-foreground">
                      {hasWritten ? "Private to your account." : "Write something above to save it."}
                    </span>
                    {journalState === "error" && (
                      <span className="text-xs text-destructive">
                        That didn't save. You can try again whenever you'd like.
                      </span>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setStep("support")}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-gold/30 px-4 text-xs font-medium text-foreground transition hover:border-gold"
            >
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
              Back to what might help
            </button>
            <button
              type="button"
              onClick={reset}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-gold/30 px-4 text-xs font-medium text-foreground transition hover:border-gold"
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden />
              Back to the wheel
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gold/15 pt-4">
            <p className="max-w-xl text-[11px] leading-relaxed text-muted-foreground">{SAFETY_NOTE}</p>
            <CrisisLink />
          </div>
        </div>
      )}

      {/* ── Step 5: needs ────────────────────────────────────────── */}
      {step === "needs" && (
        <div className="mt-5">
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Emotion wheel {feeling ? `· ${feeling.label}` : ""} · Help me choose
          </p>
          <h3 className="mt-2 font-display text-lg text-foreground">
            What might you need right now?
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Choose any that feel close. You can pick more than one, or none at all — then we'll
            suggest something to try.
          </p>
          <div className="-mx-4 mt-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 lg:grid-cols-3">
            {NEEDS.map((n) => {
              const on = needs.includes(n.key);
              const Icon = ICONS[n.icon] ?? Sparkles;
              return (
                <button
                  key={n.key}
                  type="button"
                  onClick={() => toggleNeed(n.key)}
                  aria-pressed={on}
                  style={{
                    background: `linear-gradient(150deg, ${tint(n.color, on ? 32 : 12)}, ${tint(
                      n.color,
                      on ? 58 : 34,
                    )})`,
                    borderColor: on ? ink(n.color) : `color-mix(in oklab, ${n.color} 38%, white)`,
                  }}
                  className="on-tint flex min-h-[76px] w-[76%] flex-none snap-start items-start gap-3 rounded-2xl border-2 p-3.5 text-start transition hover:-translate-y-0.5 sm:w-auto"
                >
                  <Icon className="mt-0.5 h-5 w-5 flex-none" aria-hidden style={{ color: ink(n.color) }} />
                  <span>
                    <span className="flex items-center gap-1.5 text-[15px] font-semibold" style={{ color: ink(n.color) }}>
                      {n.label}
                      {on && <Check className="h-3.5 w-3.5" aria-hidden />}
                    </span>
                    <span className="mt-0.5 block text-[11px] leading-snug text-foreground/75">{n.line}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setStep("practices")}
              style={{ background: ink(accent) }}
              className="inline-flex min-h-[48px] items-center gap-2 rounded-full px-6 text-sm font-semibold text-[var(--cream,#FBF7EF)] transition hover:-translate-y-0.5"
            >
              Choose something to try <ArrowRight className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => setStep("support")}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-gold/30 px-4 text-xs font-medium text-foreground transition hover:border-gold"
            >
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
              Back to what might help
            </button>
          </div>
        </div>
      )}

      {/* ── Step 5: practices ────────────────────────────────────── */}
      {step === "practices" && !unlocked && (
        <div className="mt-5">
          <LockedPreview color={accent} onReset={reset} />
        </div>
      )}

      {step === "practices" && unlocked && (
        <div className="mt-5">
          <h3 className="font-display text-lg text-foreground">Try something now</h3>
          <div
            className="on-tint mt-3 rounded-2xl border-2 p-4"
            style={{
              background: `linear-gradient(160deg, ${tint(accent, 14)}, ${tint(accent, 38)})`,
              borderColor: `color-mix(in oklab, ${accent} 40%, white)`,
            }}
          >
            <p className="font-display text-base" style={{ color: ink(accent) }}>
              {resetScript.title}
            </p>
            <ol className="mt-2 space-y-1.5">
              {resetScript.script.map((line) => (
                <li key={line} className="flex gap-2 text-sm leading-relaxed text-foreground/85">
                  <span
                    aria-hidden
                    className="mt-2 h-1.5 w-1.5 flex-none rounded-full"
                    style={{ background: ink(accent) }}
                  />
                  {line}
                </li>
              ))}
            </ol>
            <p className="mt-2 text-[11px] text-muted-foreground">
              An invitation, not an instruction — take only the parts that feel possible.
            </p>
          </div>

          <h3 className="mt-6 font-display text-lg text-foreground">What might help right now?</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            These are suggestions, not instructions. Take whichever feels possible — or none of them.
          </p>
          <div className="-mx-4 mt-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 lg:grid-cols-3">
            {practices.slice(0, 5).map((p) => (
              <div key={p.to + p.label} className="flex w-[76%] flex-none snap-start [&>*]:w-full sm:w-auto">
                <PracticeCard
                  practice={p}
                  color={accent}
                  locked={Boolean(p.memberOnly) && !hasMemberAccess}
                  onOpen={() => setOpenedPractice(p.label)}
                />
              </div>
            ))}
          </div>

          {onSaveCheckIn && feeling && (
            <div className="mt-5 rounded-2xl border border-gold/25 bg-cream/50 p-4">
              <p className="text-sm font-medium text-foreground">Would you like to save today's check-in?</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                Private to your account, and completely optional. It saves the date, the feeling you
                chose and what you said you needed — nothing you wrote in the reflection boxes.
              </p>
              {saveState === "saved" ? (
                <p className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--gold-deep)]">
                  <Check className="h-4 w-4" aria-hidden /> Saved to your private check-ins
                </p>
              ) : (
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={save}
                    disabled={saveState === "saving"}
                    className="inline-flex min-h-[44px] items-center rounded-full bg-[var(--gold-deep)] px-5 text-sm font-semibold text-[var(--cream,#FBF7EF)] disabled:opacity-60"
                  >
                    {saveState === "saving" ? "Saving…" : "Save this check-in"}
                  </button>
                  <button
                    type="button"
                    onClick={reset}
                    className="min-h-[44px] text-sm font-medium text-muted-foreground underline-offset-4 hover:underline"
                  >
                    No thanks
                  </button>
                  {saveState === "error" && (
                    <span className="text-xs text-destructive">
                      That didn't save. You can try again whenever you'd like.
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-gold/15 pt-4">
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setStep("support")}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-gold/30 px-4 text-xs font-medium text-foreground transition hover:border-gold"
              >
                <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
                Back to what might help
              </button>
              <button
                type="button"
                onClick={reset}
                className="min-h-[44px] text-sm font-medium text-[var(--gold-deep)] underline-offset-4 hover:underline"
              >
                Start again with another feeling
              </button>
            </div>
            <CrisisLink />
          </div>
          <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">{SAFETY_NOTE}</p>
        </div>
      )}
    </section>
  );
}
